import {
  ACCESS_REQUEST_STATUSES,
  ACCESS_RESOURCE_TYPES,
  ACCESS_RESOURCE_TYPE_LABELS,
  ACCESS_RESOURCE_TYPE_PROVIDERS,
  type AccessResourceType,
} from '@infras/shared-types';

/**
 * Access-request row helpers for /requests: the access-only filter tokens
 * (FilterTokenBar's default language), saved-view storage, "waiting on me", and the
 * label chips each access row carries.
 *
 * Kept free of React, Apollo and the router so `scripts/check-url-state.mjs` can
 * import and assert it under plain node (same split as `urlParams.ts`).
 *
 * The whole filter is ONE url param (`q`), e.g. `status:pending system:database mongo`.
 * One param keeps a filtered list a single pasteable link and lets the back button
 * walk it; tokens are parsed out of it, everything else is free text.
 */

// ── row shape (structural, so this file does not import lib/access.ts → Apollo) ──

export interface ListRow {
  id: string;
  requesterEmail: string;
  resourceType: string;
  entityId?: string;
  resourceName: string;
  maintainerEmail: string;
  maintainerSignoff?: string;
  currentLayer: number;
  status: string;
  decidedBy?: string;
  decidedAt?: string;
  durationKind?: string;
  expiresAt?: string;
  riskTier?: string;
  createdAt: string;
  updatedAt?: string;
  commentCount?: number;
  assignees?: { reviewerEmail: string; layer: number }[];
}

// ── the filter language ────────────────────────────────────────────────────────

export type FilterKey = 'status' | 'system' | 'requester' | 'assignee' | 'risk' | 'expires';

export const FILTER_KEYS: FilterKey[] = ['status', 'system', 'requester', 'assignee', 'risk', 'expires'];

export const FILTER_KEY_LABELS: Record<FilterKey, string> = {
  status: 'Status',
  system: 'System',
  requester: 'Requester',
  assignee: 'Reviewer',
  risk: 'Risk',
  expires: 'Expires',
};

/** Values the "Add filter" menu offers per key. `requester`/`assignee` also accept an email. */
export const FILTER_KEY_VALUES: Record<FilterKey, string[]> = {
  status: [...ACCESS_REQUEST_STATUSES],
  system: [...ACCESS_RESOURCE_TYPES],
  requester: ['me'],
  assignee: ['me'],
  risk: ['standard', 'elevated', 'high'],
  expires: ['soon'],
};

export interface FilterToken {
  key: FilterKey;
  value: string;
}

export interface ParsedFilter {
  tokens: FilterToken[];
  text: string;
}

const isKey = (k: string): k is FilterKey => (FILTER_KEYS as string[]).includes(k);

/** `status:pending  mongo  risk:high` → tokens [status, risk] + text "mongo". Unknown
 *  `foo:bar` words stay free text; duplicate tokens collapse. */
export function parseFilter(q: string): ParsedFilter {
  const tokens: FilterToken[] = [];
  const words: string[] = [];
  for (const w of q.split(/\s+/).filter(Boolean)) {
    const i = w.indexOf(':');
    const k = i > 0 ? w.slice(0, i).toLowerCase() : '';
    const v = i > 0 ? w.slice(i + 1).toLowerCase() : '';
    if (isKey(k) && v) {
      if (!tokens.some((t) => t.key === k && t.value === v)) tokens.push({ key: k, value: v });
    } else {
      words.push(w);
    }
  }
  return { tokens, text: words.join(' ') };
}

/** Canonical form: tokens first (in insertion order), then the free text. */
export function serializeFilter(f: ParsedFilter): string {
  return [...f.tokens.map((t) => `${t.key}:${t.value}`), f.text.trim()].filter(Boolean).join(' ');
}

/** Normalise any filter string so two spellings of the same filter compare equal. */
export function normalizeFilter(q: string): string {
  return serializeFilter(parseFilter(q));
}

export function addToken(q: string, t: FilterToken): string {
  const f = parseFilter(q);
  // status/system/risk/expires are single-valued: a second status replaces the first.
  const multi = t.key === 'requester' || t.key === 'assignee';
  const tokens = f.tokens.filter((x) => !(x.key === t.key && (!multi || x.value === t.value)));
  return serializeFilter({ tokens: [...tokens, { key: t.key, value: t.value.toLowerCase() }], text: f.text });
}

export function removeToken(q: string, t: FilterToken): string {
  const f = parseFilter(q);
  return serializeFilter({
    tokens: f.tokens.filter((x) => !(x.key === t.key && x.value === t.value)),
    text: f.text,
  });
}

export function tokenValueLabel(t: FilterToken): string {
  if (t.key === 'status' || t.key === 'risk') return cap(t.value);
  if (t.key === 'system') return ACCESS_RESOURCE_TYPE_LABELS[t.value as AccessResourceType] ?? t.value;
  if (t.key === 'expires' && t.value === 'soon') return `within ${EXPIRING_SOON_DAYS} days`;
  return t.value;
}

export function tokenOpLabel(t: FilterToken): string {
  return t.key === 'expires' ? '' : 'is';
}

// ── who / when predicates ──────────────────────────────────────────────────────

export const EXPIRING_SOON_DAYS = 30;
const DAY = 86_400_000;

const lc = (s?: string | null) => (s ?? '').toLowerCase();

/**
 * The request needs a decision from `me` right now. Mirrors who the detail page
 * shows the decision box to, narrowed to the people the request was ROUTED to:
 * an assignee at the current layer, or the maintainer when their sign-off was
 * asked for. Never your own request (four-eyes: you cannot decide it).
 */
export function isWaitingOn(r: ListRow, me: string): boolean {
  const my = lc(me);
  if (!my || r.status !== 'pending' || lc(r.requesterEmail) === my) return false;
  const assigned = (r.assignees ?? []).some(
    (a) => lc(a.reviewerEmail) === my && a.layer === r.currentLayer,
  );
  const signoff = lc(r.maintainerEmail) === my && r.maintainerSignoff === 'requested';
  return assigned || signoff;
}

/** Days until expiry (negative once lapsed); undefined for permanent / no date. */
export function daysToExpiry(r: ListRow, now: number): number | undefined {
  if (r.durationKind !== 'temporary' || !r.expiresAt) return undefined;
  const t = new Date(r.expiresAt).getTime();
  return Number.isNaN(t) ? undefined : Math.ceil((t - now) / DAY);
}

export function isExpiringSoon(r: ListRow, now: number): boolean {
  const d = daysToExpiry(r, now);
  return r.status === 'approved' && d !== undefined && d >= 0 && d <= EXPIRING_SOON_DAYS;
}

// ── saved views ───────────────────────────────────────────────────────────────

export interface ViewDef {
  slug: string;
  label: string;
  q: string;
  /** Shown only to people who can be routed a request (not new members). */
  reviewerOnly?: boolean;
}

export function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'view';
}

/** Parse the personal-views blob defensively: anything malformed is dropped. */
export function parseStoredViews(raw: string | null): { name: string; q: string }[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    return v
      .filter(
        (x): x is { name: string; q: string } =>
          !!x && typeof x.name === 'string' && typeof x.q === 'string' && x.name.trim() !== '',
      )
      .map((x) => ({ name: x.name.trim().slice(0, 40), q: normalizeFilter(x.q) }))
      .slice(0, 20);
  } catch {
    return [];
  }
}

// ── chips ─────────────────────────────────────────────────────────────────────

export type ChipTone = 'status' | 'plain' | 'risk' | 'tag' | 'layer' | 'ok' | 'warn' | 'due';
export type ChipIcon = 'check' | 'clock' | 'access';

export interface Chip {
  tone: ChipTone;
  label: string;
  /** For `status` = the raw status; for `risk` = the tier. */
  variant?: string;
  icon?: ChipIcon;
  title?: string;
}

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

export function systemKind(type: string): string {
  return `${ACCESS_RESOURCE_TYPE_LABELS[type as AccessResourceType] ?? cap(type)} access`;
}

export function requestChips(r: ListRow, me: string, now: number): Chip[] {
  // The status chip's tint comes from `statusBadgeClass` (lib/access.ts) at render time.
  const chips: Chip[] = [{ tone: 'status', label: cap(r.status), variant: r.status }];
  const provider = ACCESS_RESOURCE_TYPE_PROVIDERS[r.resourceType as AccessResourceType];
  if (provider) chips.push({ tone: 'plain', label: provider, title: 'System' });
  if (r.riskTier && r.riskTier !== 'standard')
    chips.push({ tone: 'risk', label: r.riskTier, variant: r.riskTier, title: 'Risk tier' });

  if (r.durationKind === 'temporary') {
    const d = daysToExpiry(r, now);
    const soon = isExpiringSoon(r, now);
    chips.push({
      tone: 'tag',
      label: r.expiresAt && !soon && d !== undefined && d >= 0 ? `Temporary · until ${shortDate(r.expiresAt)}` : 'Temporary',
    });
    if (soon && r.expiresAt)
      chips.push({
        tone: 'due',
        icon: 'clock',
        label: `expires ${shortDate(r.expiresAt)} · ${d === 0 ? 'today' : `in ${d} day${d === 1 ? '' : 's'}`}`,
      });
    else if (r.status === 'approved' && r.expiresAt && d !== undefined && d < 0)
      chips.push({ tone: 'plain', icon: 'clock', label: `expired ${shortDate(r.expiresAt)}` });
  } else {
    chips.push({ tone: 'plain', label: 'Permanent' });
  }

  const pending = r.status === 'pending';
  if (pending) chips.push({ tone: 'layer', label: r.currentLayer > 1 ? `L${r.currentLayer} · escalated` : `L${r.currentLayer}` });

  if (r.maintainerSignoff === 'approved') chips.push({ tone: 'ok', icon: 'check', label: 'Maintainer signed off' });
  else if (r.maintainerSignoff === 'declined') chips.push({ tone: 'warn', label: 'Maintainer declined' });
  else if (r.maintainerSignoff === 'requested' && pending) chips.push({ tone: 'plain', label: 'Maintainer sign-off requested' });

  if (isWaitingOn(r, me)) chips.push({ tone: 'warn', icon: 'access', label: 'Waiting on you' });
  else if (pending && lc(r.requesterEmail) === lc(me))
    chips.push({ tone: 'plain', label: `Next: a layer-${r.currentLayer} reviewer` });
  return chips;
}

/** "5 min ago" / "3 hours ago" / "yesterday" / "2 weeks ago" / "Oct 2". */
export function relTime(iso: string | undefined, now: number): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 14) return `${d} days ago`;
  if (d < 60) return `${Math.round(d / 7)} weeks ago`;
  return shortDate(iso);
}

/** "dennis.nguyen@optisigns.com" → "DN". Same rule as the detail page's avatars. */
export function initials(email: string): string {
  const [first, second] = email.split('@')[0].split(/[._-]+/).filter(Boolean);
  return ((first?.[0] ?? '') + (second?.[0] ?? '')).toUpperCase() || email.slice(0, 1).toUpperCase();
}

/** Who the row's avatar stack shows: current-layer reviewers while pending, the decider after. */
export function rowPeople(r: ListRow): string[] {
  if (r.status === 'pending') {
    const cur = (r.assignees ?? []).filter((a) => a.layer === r.currentLayer).map((a) => a.reviewerEmail);
    return [...new Set(cur)].slice(0, 3);
  }
  return r.decidedBy ? [r.decidedBy] : [];
}
